import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

interface NavbarProps {
  transparent?: boolean;
}

export default function Navbar({ transparent = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setMobileCatsOpen(false);
  }, [location.pathname]);

  const isLight = transparent && !scrolled;

  const handleCategorySelect = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isLight
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Left: Brand + Home */}
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className={`font-heading text-xl md:text-2xl font-bold whitespace-nowrap transition-colors duration-300 ${
                isLight ? 'text-white' : 'text-foreground-950'
              }`}
            >
              AllChinaBuy
            </Link>
            {/* Home link */}
            <Link
              to="/"
              className={`hidden md:flex items-center gap-1 text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                isLight ? 'text-white/80 hover:text-white' : 'text-foreground-700 hover:text-foreground-950'
              }`}
            >
              <i className="ri-home-4-line text-sm"></i>
              Home
            </Link>
          </div>

          {/* Center: Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/guide/what-is-allchinabuy-spreadsheet"
              className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                isLight ? 'text-white/80 hover:text-white' : 'text-foreground-700 hover:text-foreground-950'
              }`}
            >
              Sourcing Guide
            </Link>
            <a
              href="#categories"
              className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                isLight ? 'text-white/80 hover:text-white' : 'text-foreground-700 hover:text-foreground-950'
              }`}
            >
              Shopping Guide
            </a>
            <a
              href="#comparison"
              className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                isLight ? 'text-white/80 hover:text-white' : 'text-foreground-700 hover:text-foreground-950'
              }`}
            >
              Comparison
            </a>
          </div>

          {/* Right: Actions */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="#faq"
              className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                isLight ? 'text-white/80 hover:text-white' : 'text-foreground-700 hover:text-foreground-950'
              }`}
            >
              Safety
            </a>
            <a
              href="#faq"
              className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                isLight ? 'text-white/80 hover:text-white' : 'text-foreground-700 hover:text-foreground-950'
              }`}
            >
              FAQ
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-300 cursor-pointer ${
                isLight
                  ? 'bg-white text-foreground-950 hover:bg-white/90'
                  : 'bg-foreground-950 text-background-50 hover:bg-foreground-800'
              }`}
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
              isLight ? 'text-white' : 'text-foreground-950'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 px-6 py-4 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
          <Link to="/" className="flex items-center gap-2 text-foreground-700 text-sm font-medium py-2.5" onClick={() => setMobileOpen(false)}>
            <i className="ri-home-4-line"></i> Home
          </Link>
          <Link to="/guide/what-is-allchinabuy-spreadsheet" className="flex items-center gap-2 text-foreground-700 text-sm font-medium py-2.5" onClick={() => setMobileOpen(false)}>
            <i className="ri-book-open-line"></i> Sourcing Guide
          </Link>
          <a href="#categories" className="flex items-center gap-2 text-foreground-700 text-sm font-medium py-2.5" onClick={() => setMobileOpen(false)}>
            <i className="ri-shopping-bag-3-line"></i> Shopping Guide
          </a>

          {/* Mobile Categories Toggle */}
          <button
            onClick={() => setMobileCatsOpen(!mobileCatsOpen)}
            className="flex items-center justify-between w-full text-foreground-700 text-sm font-medium py-2.5 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-grid-line"></i> Categories
            </span>
            <i className={`text-sm transition-transform duration-200 ${mobileCatsOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
          </button>

          {mobileCatsOpen && (
            <div className="grid grid-cols-2 gap-1 pl-8 pb-2">
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => handleCategorySelect(cat.url)}
                  className="flex items-center gap-2 text-foreground-600 text-xs py-2 text-left hover:text-primary-600 transition-colors duration-150 cursor-pointer whitespace-nowrap"
                >
                  <i className={`${cat.icon} text-secondary-500 text-xs`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          )}

          <a href="#comparison" className="flex items-center gap-2 text-foreground-700 text-sm font-medium py-2.5" onClick={() => setMobileOpen(false)}>
            <i className="ri-scales-3-line"></i> Comparison
          </a>
          <a href="#faq" className="flex items-center gap-2 text-foreground-700 text-sm font-medium py-2.5" onClick={() => setMobileOpen(false)}>
            <i className="ri-shield-check-line"></i> Safety
          </a>
          <a href="#faq" className="flex items-center gap-2 text-foreground-700 text-sm font-medium py-2.5" onClick={() => setMobileOpen(false)}>
            <i className="ri-question-line"></i> FAQ
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="bg-foreground-950 text-background-50 px-5 py-2.5 rounded-full text-sm font-semibold text-center whitespace-nowrap cursor-pointer mt-2"
          >
            Buy It Now
          </a>
        </div>
      )}
    </nav>
  );
}